import { monthlyTargets } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

export const selectMonthlyTargetSchema = createSelectSchema(monthlyTargets)

// An override must carry its reason. That rule lives in a database CHECK
// constraint rather than here, because createCrudRouter derives its update
// schema with .partial() and a refined schema has no such method.
export const insertMonthlyTargetSchema = createInsertSchema(
  monthlyTargets,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const updateMonthlyTargetSchema = createInsertSchema(monthlyTargets)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .partial()

export const selectMonthlyTargetWithTeamSchema =
  selectMonthlyTargetSchema.extend({
    team: z.object({ id: z.number(), name: z.string() }).optional().nullable(),
  })

// The target grid edits one cell at a time, so a batch is a list of cells
// rather than a list of rows: naming the field explicitly is what stops a
// sales-target edit from clearing the revenue target sharing its row.
export const saveMonthlyTargetCellsSchema = z.object({
  cells: z
    .array(
      z.object({
        month: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        // Null is the company-wide row.
        teamId: z.number().nullable(),
        field: z.enum([
          'revenueTarget',
          'salesTarget',
          'eurPerDayOverride',
          'fteDelta',
        ]),
        // Decimal as a string, the way drizzle stores it. Null clears the cell.
        value: z.string().nullable(),
        note: z.string().nullable().optional(),
      }),
    )
    .min(1)
    // A whole grid is four measures over six months for a handful of teams; a
    // batch larger than this is a bug, not a bigger plan.
    .max(200)
    .refine(
      (cells) =>
        cells.every(
          (c) =>
            c.value === null ||
            (c.field !== 'eurPerDayOverride' && c.field !== 'fteDelta') ||
            c.note?.trim(),
        ),
      // The database CHECK says the same thing about the stored row; saying it
      // here too is what turns a constraint violation into a usable message.
      { message: 'Käsin asetettu arvo vaatii perustelun' },
    ),
})

export type SaveMonthlyTargetCells = z.infer<
  typeof saveMonthlyTargetCellsSchema
>

export type MonthlyTarget = z.infer<typeof selectMonthlyTargetSchema>
export type MonthlyTargetWithTeam = z.infer<
  typeof selectMonthlyTargetWithTeamSchema
>
export type CreateMonthlyTarget = z.infer<typeof insertMonthlyTargetSchema>
export type UpdateMonthlyTarget = z.infer<typeof updateMonthlyTargetSchema>
